<script lang="ts" setup>
import TagSelector from "@/components/mobile/common/TagSelector.vue";
import { useModule } from "@/stores/module";
import { Worldview } from "@/types/entity";
import { useDefaultWorldviewImg } from "@/use/common";
import { useRouteParams } from "@vueuse/router";
import { computed, ref } from "vue";
import { Popup, showConfirmDialog, showDialog } from "vant";
import { useLocalStorage } from "@vueuse/core";
import { isAndroidWebview } from "@/modules/common";
import { useI18n } from "vue-i18n";

type UpdateKey = keyof Worldview | Array<keyof Worldview>;
const emit = defineEmits<{
  (e: "update:worldview", worldview: UpdateKey): void;
}>();

const showPlanetGeneratorPopup = ref(false);
const moduleStore = useModule();
const uuid = useRouteParams("uuid", "");
const { t } = useI18n();

const worldview = computed(() => {
  return moduleStore.module!.entity as Worldview;
});

const updateTag = (val: string[]) => {
  worldview.value.tag = val.join(",");
  emit("update:worldview", "tag");
};

const richEditorCallback = (content: string, type: keyof Worldview) => {
  worldview.value[type] = content as never;
  emit("update:worldview", type);
};

const showAndroidVersionTip = useLocalStorage(
  "showAndroidVersionTip",
  () => true
);

const onEnterPlanetGeneratorClick = async () => {
  if (showAndroidVersionTip.value && isAndroidWebview) {
    await showDialog({
      message: t("Message.androidVersionTip"),
      confirmButtonText: t("Buttons.confirm"),
    });
    showAndroidVersionTip.value = false;
  } else {
    showPlanetGeneratorPopup.value = true;
  }
};
</script>
<template>
  <div class="pt-6">
    <div class="flex flex-col gap-2 items-center mb-2">
      <div
        class="w-[150px] aspect-[1/1] flex justify-center items-center border border-base-content/10 rounded-xl relative overflow-hidden max-w-[20rem]"
      >
        <VanImage
          :src="worldview.media?.url || useDefaultWorldviewImg()"
          class="w-full"
          aspect="1/1"
          crossorigin="anonymous"
        />
      </div>

      <button
        class="btn btn-sm btn-primary rounded"
        @click="onEnterPlanetGeneratorClick"
      >
        {{ $t("module.worldview.basicInfo.media") }}
      </button>
    </div>

    <VanCellGroup inset>
      <!-- 名字 -->
      <VanField
        :label="$t('module.worldview.basicInfo.name')"
        v-model="worldview.name"
        :placeholder="$t('Message.none')"
        :rules="[
          {
            required: true,
            message: $t('module.worldview.basicInfo.rules.name'),
          },
        ]"
        @blur="emit('update:worldview', 'name')"
        required
      />
      <!-- 世界观描述 -->
      <CustomRichTextCell
        :title="$t('module.worldview.basicInfo.description')"
        :module-name="$t('module.worldview.label')"
        :content="worldview.description"
        :cb="(content:string) => richEditorCallback(content, 'description')"
      />
    </VanCellGroup>

    <div class="p-4 bg-base-200 m-4 rounded-lg flex flex-col gap-2">
      <div class="flex justify-between">
        <span>{{ $t("User.mate.basicInfo.tag") }}</span>
        <span class="text-base-content/50">
          {{ $t("User.mate.basicInfo.rules.tag") }}
        </span>
      </div>
      <TagSelector
        :tag="worldview.tag?.split(',') || []"
        @update:tag="updateTag"
      />
    </div>
    <Popup
      v-model:show="showPlanetGeneratorPopup"
      position="bottom"
      round
      class="h-full"
    >
      <PlanetGenerator
        :worldviewUuid="worldview.uuid"
        :uuid="uuid"
        @close="showPlanetGeneratorPopup = false"
      />
    </Popup>
  </div>
</template>
